import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchVoiceTokenAPI } from '../context-api/api-services/twilio.service';
import { createDevice, disconnectCall, isValidDestination, startCall } from '../utils/twilio-device';

const CALL_STATUS = {
    IDLE: 'idle',
    REGISTERING: 'registering',
    READY: 'ready',
    CALLING: 'calling',
    IN_CALL: 'in_call',
    ENDED: 'ended',
    ERROR: 'error',
};

/**
 * @param {string} identity  Agent identity used to fetch the Twilio token.
 */
const useTwilioVoice = (identity) => {
    const [status, setStatus] = useState(CALL_STATUS.IDLE);
    const [last_error, setLastError] = useState(null);
    const [is_muted, setIsMuted] = useState(false);
    const [incoming_call, setIncomingCall] = useState(null);

    const device_ref = useRef(null);
    const active_call_ref = useRef(null);
    const is_connecting_ref = useRef(false);
    const init_device_ref = useRef(null);
    const [active_call_sid, setActiveCallSid] = useState(null);

    const clear_error = useCallback(() => {
        setLastError(null);
    }, []);

    const setup_device_listeners = useCallback((device) => {
        device.on('registered', () => {
            setStatus(CALL_STATUS.READY);
            setLastError(null);
        });

        device.on('error', (err) => {
            const code = err?.code;
            let msg = err?.message || 'Unknown Twilio device error.';

            if (code === 31100) {
                msg = 'Twilio setup error: the access token has an invalid or missing TwiML Application SID. Check your backend configuration.';
            } else if (code === 20101 || code === 20104) {
                msg = 'Twilio access token is invalid or expired. Retrying…';
                setTimeout(() => init_device_ref.current?.(), 1500);
                return;
            } else if (
                msg.toLowerCase().includes('permission') ||
                msg.toLowerCase().includes('microphone') ||
                msg.toLowerCase().includes('notallowederror')
            ) {
                msg = 'Microphone access denied. Please allow microphone permissions in your browser and try again.';
            }

            console.log('====================================>>>>>>>>>>>>>>>>[TwilioVoice][useTwilioVoice] device.error -> setLastError:', msg);
            setLastError(msg);
            setStatus(CALL_STATUS.ERROR);
            is_connecting_ref.current = false;
        });

        device.on('incoming', (call) => {
            setIncomingCall(call);

            call.on('disconnect', () => {
                setIncomingCall(null);
                active_call_ref.current = null;
                setStatus(CALL_STATUS.ENDED);
                setTimeout(() => setStatus(CALL_STATUS.READY), 800);
            });

            call.on('cancel', () => {
                setIncomingCall(null);
            });
        });
    }, []);

    const setup_call_listeners = useCallback((call) => {
        call.on('accept', () => {
            const sid = call.parameters?.CallSid ?? null;
            setActiveCallSid(sid);
            setStatus(CALL_STATUS.IN_CALL);
            is_connecting_ref.current = false;
        });

        call.on('disconnect', () => {
            active_call_ref.current = null;
            setIsMuted(false);
            setStatus(CALL_STATUS.ENDED);
            setTimeout(() => {
                setStatus(CALL_STATUS.READY);
                setActiveCallSid(null);
            }, 800);
            is_connecting_ref.current = false;
        });

        call.on('error', (err) => {
            const msg = err?.message || 'Call error occurred.';
            console.log('====================================>>>>>>>>>>>>>>>>[TwilioVoice][useTwilioVoice] call.error -> setLastError:', msg);
            setLastError(msg);
            setStatus(CALL_STATUS.ERROR);
            active_call_ref.current = null;
            is_connecting_ref.current = false;
        });
    }, []);

    const init_device = useCallback(async () => {
        if (!identity) return;

        setStatus(CALL_STATUS.REGISTERING);
        setLastError(null);

        const result = await fetchVoiceTokenAPI(identity);

        if (result?.hasError) {
            const error_msg = result.message || 'Failed to fetch token.';
            console.log('====================================>>>>>>>>>>>>>>>>[TwilioVoice][useTwilioVoice] init_device.fetch_token_error -> setLastError:', error_msg);
            setLastError(error_msg);
            setStatus(CALL_STATUS.ERROR);
            return;
        }

        const token = result?.data?.token ?? result?.token;

        if (!token) {
            const error_msg = 'No token received from server.';
            console.log('====================================>>>>>>>>>>>>>>>>[TwilioVoice][useTwilioVoice] init_device.empty_token -> setLastError:', error_msg);
            setLastError(error_msg);
            setStatus(CALL_STATUS.ERROR);
            return;
        }

        if (device_ref.current) {
            try { device_ref.current.destroy(); } catch (_) {}
        }

        const device = createDevice(token);
        device_ref.current = device;
        setup_device_listeners(device);
        // register() may reject when the token is invalid; the 'error' event
        // on the device already handles the UI state, so just suppress the
        // unhandled rejection here.
        device.register().catch(() => {});
    }, [identity, setup_device_listeners]);

    init_device_ref.current = init_device;

    // Cleanup only — device is initialised lazily via prepare(), not on mount.
    useEffect(() => {
        return () => {
            if (active_call_ref.current) {
                try { active_call_ref.current.disconnect(); } catch (_) {}
            }
            if (device_ref.current) {
                try { device_ref.current.destroy(); } catch (_) {}
            }
        };
    }, []);

    const call = useCallback(async (destination) => {
        if (is_connecting_ref.current) return;
        if (!device_ref.current) {
            const error_msg = 'Device not ready. Please wait.';
            console.log('====================================>>>>>>>>>>>>>>>>[TwilioVoice][useTwilioVoice] call.device_not_ready -> setLastError:', error_msg);
            setLastError(error_msg);
            return;
        }

        const trimmed = (destination || '').trim();

        if (!isValidDestination(trimmed)) {
            const error_msg = 'Invalid destination. Use E.164 format (+14155551234) or client:name.';
            console.log('====================================>>>>>>>>>>>>>>>>[TwilioVoice][useTwilioVoice] call.invalid_destination -> setLastError:', error_msg);
            setLastError(error_msg);
            return;
        }

        is_connecting_ref.current = true;
        setLastError(null);
        setStatus(CALL_STATUS.CALLING);

        try {
            const active_call = await startCall(device_ref.current, trimmed);
            active_call_ref.current = active_call;
            setup_call_listeners(active_call);
        } catch (err) {
            const msg = err?.message || 'Failed to connect call.';

            if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('microphone')) {
                const error_msg = 'Microphone access denied. Please allow mic permissions and try again.';
                console.log('====================================>>>>>>>>>>>>>>>>[TwilioVoice][useTwilioVoice] call.mic_permission_denied -> setLastError:', error_msg);
                setLastError(error_msg);
            } else {
                console.log('====================================>>>>>>>>>>>>>>>>[TwilioVoice][useTwilioVoice] call.connect_error -> setLastError:', msg);
                setLastError(msg);
            }

            setStatus(CALL_STATUS.ERROR);
            is_connecting_ref.current = false;
        }
    }, [setup_call_listeners]);

    const hangup = useCallback(() => {
        disconnectCall(active_call_ref.current);
    }, []);

    const toggle_mute = useCallback(() => {
        const active_call = active_call_ref.current;
        if (!active_call) return;
        const next = !is_muted;
        active_call.mute(next);
        setIsMuted(next);
    }, [is_muted]);

    const accept_incoming = useCallback(() => {
        if (!incoming_call) return;
        incoming_call.accept();
        active_call_ref.current = incoming_call;
        setIncomingCall(null);
        setStatus(CALL_STATUS.IN_CALL);
        setup_call_listeners(incoming_call);
    }, [incoming_call, setup_call_listeners]);

    const reject_incoming = useCallback(() => {
        if (!incoming_call) return;
        incoming_call.reject();
        setIncomingCall(null);
    }, [incoming_call]);

    const prepare = useCallback(() => {
        // No-op if device is already initialising or ready.
        if (
            status === CALL_STATUS.REGISTERING ||
            status === CALL_STATUS.READY ||
            status === CALL_STATUS.CALLING ||
            status === CALL_STATUS.IN_CALL
        ) return;
        init_device();
    }, [status, init_device]);

    return {
        status,
        last_error,
        is_muted,
        incoming_call,
        active_call_sid,
        is_ready: status === CALL_STATUS.READY,
        is_calling: status === CALL_STATUS.CALLING,
        is_in_call: status === CALL_STATUS.IN_CALL,
        call,
        hangup,
        toggle_mute,
        accept_incoming,
        reject_incoming,
        clear_error,
        prepare,
        retry_init: prepare,
        CALL_STATUS,
    };
};

export default useTwilioVoice;
